import { strToU8, zipSync } from "fflate";
import { Bot, Check, Copy, Download, FileArchive, Package, Sparkles } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { appPrompt, bundlePrompt, genericPrompt } from "@/exporters/agent-prompt";
import { EXPORTERS } from "@/exporters/registry";
import { type ExportFile, makeContext } from "@/exporters/types";
import { useEditor } from "@/store/editor";
import { useUi } from "@/store/ui";
import { Button } from "@/ui/kit/button";
import { cn } from "@/ui/kit/cn";
import { Dialog } from "@/ui/kit/dialog";
import { Segmented } from "@/ui/kit/segmented";
import { Switch } from "@/ui/kit/switch";

type Target = "all" | "generic" | (typeof EXPORTERS)[number]["id"];

export function download(filename: string, data: BlobPart, type = "text/plain") {
  const url = URL.createObjectURL(new Blob([data], { type }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

async function copy(text: string, what: string) {
  await navigator.clipboard.writeText(text);
  toast.success(`${what} copied`, { description: `${text.length.toLocaleString()} characters` });
}

function CopyButton({ text, label, primary }: { text: string; label: string; primary?: boolean }) {
  const [done, setDone] = useState(false);
  return (
    <Button
      variant={primary ? "primary" : "outline"}
      onClick={async () => {
        await copy(text, label);
        setDone(true);
        setTimeout(() => setDone(false), 1500);
      }}
    >
      {done ? <Check /> : <Copy />} Copy {label.toLowerCase()}
    </Button>
  );
}

export function ExportDialog() {
  const open = useUi((s) => s.dialog === "export");
  const close = () => useUi.getState().openDialog(null);
  const theme = useEditor((s) => s.draft);
  const resolved = useEditor((s) => s.resolved);
  const ctx = useMemo(() => makeContext(theme, resolved), [theme, resolved]);
  const [target, setTarget] = useState<Target>("all");
  const [view, setView] = useState<"prompt" | "files">("prompt");
  const [includeJson, setIncludeJson] = useState(true);
  const opts = useMemo(() => ({ includeThemeJson: includeJson }), [includeJson]);

  const exporter = EXPORTERS.find((e) => e.id === target);
  const result = useMemo(
    () => (open && exporter ? exporter.run(ctx) : null),
    [open, exporter, ctx],
  );
  const prompt = useMemo(() => {
    if (!open) return "";
    if (target === "all") return bundlePrompt(ctx, opts);
    if (target === "generic") return genericPrompt(ctx, opts);
    return exporter ? appPrompt(ctx, exporter, opts) : "";
  }, [open, target, ctx, exporter, opts]);

  const allFiles = (): ExportFile[] =>
    EXPORTERS.filter((e) => e.app || e.id === "theme-json").flatMap((e) => e.run(ctx).files);
  const zip = () => {
    const entries = Object.fromEntries(
      [
        ...allFiles(),
        { filename: "PROMPT.md", content: bundlePrompt(ctx, opts), path: "", language: "md" },
      ].map((f) => [`${ctx.appName}/${f.filename}`, strToU8(f.content)]),
    );
    download(`${ctx.appName}.zip`, zipSync(entries, { level: 6 }) as BlobPart, "application/zip");
  };

  const files = target === "all" ? allFiles() : (result?.files ?? []);
  const targets: { id: Target; label: string; hint: string; group: string }[] = [
    { id: "all", label: "All my apps", hint: "One prompt for all six apps", group: "Bundles" },
    { id: "generic", label: "Any other app", hint: "Spec plus mapping guidance", group: "Bundles" },
    ...EXPORTERS.map((e) => ({
      id: e.id,
      label: e.label,
      hint: e.description,
      group: e.app ? "Apps" : "Formats",
    })),
  ];

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => !o && close()}
      title="Export"
      description={`Copy a self-contained prompt and paste it to Claude Code, Cursor or any agent: "Configure <app> with this color scheme: …"`}
      className="h-[min(88vh,860px)] w-[min(96vw,1180px)]"
    >
      <div className="flex h-full min-h-0">
        <nav className="w-64 shrink-0 space-y-3 overflow-y-auto border-line border-r p-3">
          {["Bundles", "Apps", "Formats"].map((group) => (
            <div key={group}>
              <div className="px-2 pb-1 text-[10px] text-faint uppercase tracking-wider">
                {group}
              </div>
              {targets
                .filter((t) => t.group === group)
                .map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setTarget(t.id)}
                    className={cn(
                      "block w-full rounded-md px-2 py-1.5 text-left",
                      target === t.id ? "bg-raised" : "hover:bg-raised/50",
                    )}
                    data-testid={`export-target-${t.id}`}
                  >
                    <div className="flex items-center gap-1.5 text-sm">
                      {t.id === "all" ? (
                        <Package className="size-3.5 text-accent" />
                      ) : t.id === "generic" ? (
                        <Sparkles className="size-3.5 text-accent" />
                      ) : null}
                      {t.label}
                    </div>
                    <div className="text-[11px] text-faint leading-snug">{t.hint}</div>
                  </button>
                ))}
            </div>
          ))}
        </nav>
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex flex-wrap items-center gap-2 border-line border-b px-4 py-2.5">
            <Segmented
              value={view}
              onChange={setView}
              options={[
                { value: "prompt", label: "Agent prompt" },
                { value: "files", label: `Files (${files.length})` },
              ]}
            />
            <Switch checked={includeJson} onChange={setIncludeJson} label="Include theme JSON" />
            <span className="flex-1" />
            {view === "prompt" && <CopyButton text={prompt} label="Prompt" primary />}
            <Button variant="outline" onClick={zip}>
              <FileArchive /> Download all (.zip)
            </Button>
          </div>
          {result && result.notes.length > 0 && view === "prompt" && (
            <div className="border-line border-b bg-bg/50 px-4 py-2 text-muted text-xs">
              {result.notes.map((n) => (
                <p key={n}>• {n}</p>
              ))}
            </div>
          )}
          <div className="min-h-0 flex-1 overflow-auto p-4">
            {view === "prompt" ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-muted text-xs">
                  <Bot className="size-4" /> Paste this to your agent as-is.
                </div>
                <pre
                  className="whitespace-pre-wrap break-words rounded-lg border border-line bg-bg p-4 font-mono text-[12px] text-fg leading-relaxed"
                  data-testid="export-prompt"
                >
                  {prompt}
                </pre>
              </div>
            ) : (
              <div className="space-y-4">
                {files.map((f) => (
                  <div key={f.filename} className="overflow-hidden rounded-lg border border-line">
                    <div className="flex items-center gap-2 border-line border-b bg-raised px-3 py-1.5">
                      <span className="min-w-0 flex-1 truncate font-mono text-xs">{f.path}</span>
                      <Button size="sm" variant="ghost" onClick={() => copy(f.content, f.filename)}>
                        <Copy /> Copy
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => download(f.filename, f.content)}
                      >
                        <Download /> Download
                      </Button>
                    </div>
                    <pre className="max-h-96 overflow-auto bg-bg p-3 font-mono text-[11.5px] leading-relaxed">
                      {f.content}
                    </pre>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
}
